import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "../../utils/toast";
import { Eye, EyeOff } from "lucide-react";
import logo from "../../assets/gca.png";

const RESEND_COOLDOWN_SECONDS = 60;

// The registration form's content only — no outer .login-container/.login-card
// shell, so it can render either inside Register's own standalone page (no
// brand panel) or swapped into Login's .login-card in place of the sign-in
// form (keeping the brand panel and theme). Pass onSwitchToLogin when
// rendering inline from Login so "Login Here" flips back instead of
// navigating to a different route.
function RegisterForm({ onSwitchToLogin }) {
    const [formData, setFormData] = useState({
        name: "",
        companyName: "",
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    const [stage, setStage] = useState("register"); // "register" | "otp" | "done"
    const [registeredEmail, setRegisteredEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);

    const [resendCooldown, setResendCooldown] = useState(0);
    const cooldownRef = useRef(null);

    useEffect(() => {
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, []);

    function startResendCooldown() {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        cooldownRef.current = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (e.target.name === "email") setEmailError("");
        if (e.target.name === "password" || e.target.name === "confirmPassword") setConfirmPasswordError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setEmailError("");
        setConfirmPasswordError("");

        if (formData.password !== formData.confirmPassword) {
            setConfirmPasswordError("Passwords do not match.");
            return;
        }

        setLoading(true);

        try {
            const { confirmPassword: _confirmPassword, ...registrationData } = formData;
            const resp = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/auth/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(registrationData),
            });

            const data = await resp.json();

            if (resp.ok && data.status === "otp_sent") {
                setRegisteredEmail(formData.email.trim().toLowerCase());
                startResendCooldown();
                setStage("otp");
            } else if (!resp.ok) {
                const msg = data.error || data.message || "Registration failed";
                if (data.error && data.error.toLowerCase().includes("business email")) {
                    setEmailError(data.error);
                } else {
                    toast.error(msg);
                }
            } else {
                toast.error(data.message || "Unexpected response from server.");
            }
        } catch (err) {
            toast.error("Network error. Please check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setVerifyLoading(true);
        setOtpError("");

        try {
            const resp = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: registeredEmail, otp: otp.trim() }),
            });

            const data = await resp.json();

            if (resp.ok && data.status === "verified") {
                setStage("done");
            } else {
                setOtpError(data.message || "Verification failed. Please try again.");
            }
        } catch (err) {
            setOtpError("Network error. Please check your connection and try again.");
        } finally {
            setVerifyLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0 || resendLoading) return;
        setResendLoading(true);
        setOtpError("");

        try {
            const resp = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/auth/resend-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: registeredEmail }),
            });

            const data = await resp.json();

            if (resp.ok) {
                toast.success("A new verification code has been sent.");
                startResendCooldown();
            } else if (resp.status === 429) {
                setOtpError(data.message || "Too many resend requests. Please wait before trying again.");
            } else {
                toast.error(data.message || "Failed to resend code.");
            }
        } catch (err) {
            toast.error("Network error. Please check your connection and try again.");
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <>
            <img src={logo} alt="GCA Logo" className="login-logo" />
            <h2 className="login-title">VisuN AI</h2>
            <p className="login-subtitle">Start Your Free Trial</p>

            {stage === "register" && (
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="reg-name">Full Name</label>
                        <input
                            id="reg-name"
                            type="text"
                            name="name"
                            value={formData.name}
                            required
                            onChange={handleChange}
                            autoComplete="name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="reg-company">Company/Tenant Name</label>
                        <input
                            id="reg-company"
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            required
                            onChange={handleChange}
                            autoComplete="organization"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="reg-email">Email Address</label>
                        <input
                            id="reg-email"
                            type="email"
                            name="email"
                            value={formData.email}
                            required
                            onChange={handleChange}
                            autoComplete="email"
                            aria-describedby={emailError ? "email-error" : undefined}
                            style={emailError ? { borderColor: "#c0392b" } : undefined}
                        />
                        {emailError && (
                            <p id="email-error" className="otp-inline-error" role="alert">
                                {emailError}
                            </p>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="reg-password">Password</label>
                        <div className="password-input-wrapper">
                            <input
                                id="reg-password"
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                required
                                minLength={8}
                                onChange={handleChange}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="reg-confirm-password">Confirm Password</label>
                        <div className="password-input-wrapper">
                            <input
                                id="reg-confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                required
                                minLength={8}
                                onChange={handleChange}
                                autoComplete="new-password"
                                aria-describedby={confirmPasswordError ? "confirm-password-error" : undefined}
                                style={confirmPasswordError ? { borderColor: "#c0392b" } : undefined}
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                tabIndex={-1}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {confirmPasswordError && (
                            <p id="confirm-password-error" className="otp-inline-error" role="alert">
                                {confirmPasswordError}
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="login-btn"
                        disabled={loading}
                        aria-busy={loading}
                    >
                        {loading ? "Creating Account..." : "Create Account"}
                    </button>
                </form>
            )}

            {stage === "otp" && (
                <div className="otp-screen">
                    <p className="otp-instruction">
                        A 6-digit verification code was sent to <strong>{registeredEmail}</strong>.
                        Enter it below to verify your email address.
                    </p>

                    <form onSubmit={handleVerify}>
                        <div className="form-group">
                            <label htmlFor="otp-input">Verification Code</label>
                            <input
                                id="otp-input"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]{6}"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => {
                                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                                    setOtpError("");
                                }}
                                required
                                placeholder="000000"
                                autoComplete="one-time-code"
                                aria-describedby={otpError ? "otp-error" : undefined}
                                style={{ letterSpacing: "0.2em", textAlign: "center", fontSize: "1.3rem" }}
                            />
                        </div>

                        {otpError && (
                            <p id="otp-error" className="otp-inline-error" role="alert">
                                {otpError}
                            </p>
                        )}

                        <button
                            type="submit"
                            className="login-btn"
                            disabled={verifyLoading || otp.length !== 6}
                            aria-busy={verifyLoading}
                        >
                            {verifyLoading ? "Verifying..." : "Verify Email"}
                        </button>
                    </form>

                    <div className="otp-resend-row">
                        {resendCooldown > 0 ? (
                            <span className="otp-resend-timer">
                                Resend available in {resendCooldown}s
                            </span>
                        ) : (
                            <button
                                type="button"
                                className="otp-resend-btn"
                                onClick={handleResend}
                                disabled={resendLoading}
                                aria-busy={resendLoading}
                            >
                                {resendLoading ? "Sending..." : "Resend OTP"}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {stage === "done" && (
                <div className="registration-success-message">
                    <div className="registration-success-box">
                        Email verified! Your account is pending admin approval. We'll notify you when it's ready.
                    </div>
                </div>
            )}

            <div className="registration-prompt">
                <p className="registration-prompt-text">
                    Already have an account?{" "}
                    {onSwitchToLogin ? (
                        <button type="button" className="register-link-btn" onClick={onSwitchToLogin}>
                            Login Here
                        </button>
                    ) : (
                        <Link to="/login" className="register-link-btn">Login Here</Link>
                    )}
                </p>
            </div>
        </>
    );
}

export default RegisterForm;
